import { useCallback, useEffect, useState } from "react";
import { closeModal, Modal, openModal, SpinningLoader } from "risa-oasis-ui_v2";
import { fetchAppealDetail } from "../../../api/postCall/fetchAppealDetail";
import { ModalId } from "../../../enums/modalId";
import { logError } from "../../../utils/customLogger";
import {
  AppealDetailsViewModel,
  buildAppealDetailsViewModel,
} from "./utils/buildAppealDetailsViewModel";

interface AppealDetailsModalProps {
  identifier: string;
  onClose: () => void;
}

interface AppealDetailsRowProps {
  label: string;
  value: string;
  showDivider?: boolean;
}

/**
 * Renders one label/value row matching the Appeal Details design.
 *
 * @param props - Row label, value, and optional dashed bottom divider
 * @returns Appeal detail row markup
 */
const AppealDetailsRow = (props: AppealDetailsRowProps) => (
  <div
    className={`flex w-full items-baseline py-5 ${
      props.showDivider ? "border-b border-dashed border-primaryGray-15" : ""
    }`}
  >
    <span className="w-[9.5rem] shrink-0 text-small font-bold leading-small text-primaryGray-1">
      {props.label}
    </span>
    <span className="min-w-0 flex-1 break-words text-small leading-small text-primaryGray-1">
      {props.value}
    </span>
  </div>
);

/**
 * Modal that loads and displays appeal detail fields for a sent appeal case.
 * Layout matches the Appeal Details design: From Delivery, To Appeal,
 * Appeal Reason, and a single full-width Back button in the modal footer.
 *
 * @param props.identifier - Order identifier used to load `pharmacy/appeal/detail`
 * @param props.onClose - Closes the modal
 * @returns Appeal Details modal element
 */
const AppealDetailsModal = ({
  identifier,
  onClose,
}: AppealDetailsModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [details, setDetails] = useState<AppealDetailsViewModel | null>(null);

  const handleClose = useCallback(() => {
    closeModal(ModalId.APPEAL_DETAILS_MODAL);
    onClose();
  }, [onClose]);

  const loadAppealDetails = useCallback(async () => {
    const trimmedIdentifier = identifier.trim();
    if (!trimmedIdentifier) {
      setErrorMessage("Order identifier is missing.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await fetchAppealDetail({
        identifier: trimmedIdentifier,
      });
      setDetails(buildAppealDetailsViewModel(response));
    } catch (error) {
      logError(error as Error, "AppealDetailsModal.loadAppealDetails");
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load appeal details.";
      setErrorMessage(message);
      setDetails(null);
    } finally {
      setIsLoading(false);
    }
  }, [identifier]);

  useEffect(() => {
    openModal(ModalId.APPEAL_DETAILS_MODAL);
    void loadAppealDetails();
  }, [loadAppealDetails]);

  return (
    <Modal
      dialogId={ModalId.APPEAL_DETAILS_MODAL}
      title="Appeal Details"
      onClose={handleClose}
      onCancel={handleClose}
      onSave={handleClose}
      saveButtonText="Back"
      cancelText="Back"
      hideFooter={false}
      showSingleButton={true}
    >
      <div className="flex w-full flex-col py-2">
        {isLoading ? (
          <div className="flex min-h-[10rem] flex-col items-center justify-center gap-3 py-8">
            <SpinningLoader />
            <span className="text-small font-regular  text-primaryGray-6">
              Loading appeal details…
            </span>
          </div>
        ) : errorMessage ? (
          <div className="flex min-h-[10rem] flex-col items-center justify-center px-4 py-8 text-center">
            <p className="text-small font-bold leading-small text-primaryGray-1">
              Could not load appeal details
            </p>
            <p className="mt-1 text-tiny font-regular leading-tiny text-primaryGray-6">
              {errorMessage}
            </p>
          </div>
        ) : (
          <div className="w-full">
            <AppealDetailsRow
              label="From Delivery"
              value={details?.fromDelivery ?? "--"}
              showDivider
            />
            <AppealDetailsRow
              label="To Appeal"
              value={details?.toAppeal ?? "--"}
              showDivider
            />
            <AppealDetailsRow
              label="Appeal Reason:"
              value={details?.appealReason ?? "--"}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AppealDetailsModal;
